import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Chip } from '@mui/material';
import SidebarSection from './SidebarSection';
import {
  CurrencyRupee as CurrencyRupeeIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

import { useLeadDetails } from '../../../../contexts/LeadDetailsContext';

const LAKH = 100000;
const CRORE = 10000000;

const commonButtonStyle = {
  fontSize: '0.7rem',
  fontWeight: 600,
  py: 0.3,
  px: 1.2,
  minWidth: '45px',
  height: '24px',
  textTransform: 'none',
  borderRadius: 1,
  boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  '&:hover': {
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
  }
};

const BudgetSection = ({ budget = { min: 2500000, max: 100000000 }, leadId, updateLeadPreferences }) => {
  // Convert rupees to display format (Lakh/Crore)
  const toDisplayValue = (value) => {
    if (value >= CRORE) {
      return (value / CRORE).toFixed(value % CRORE === 0 ? 0 : 1);
    }
    return (value / LAKH).toFixed(value % LAKH === 0 ? 0 : 0);
  };

  // Convert display value to rupees
  const toRupees = (displayValue, unit) => {
    const numValue = parseFloat(displayValue) || 0;
    if (unit === 'Cr') {
      return Math.round(numValue * CRORE);
    }
    return Math.round(numValue * LAKH);
  };

  // Get unit for display
  const getUnit = (value) => {
    return value >= CRORE ? 'Cr' : 'L';
  };

  // Get step value based on unit
  const getStepValue = (unit) => {
    return unit === 'Cr' ? 0.1 : 5;
  };

  // Get min/max limits for input validation
  const getInputLimits = (unit) => {
    if (unit === 'L') {
      return { min: 0, max: 1000 };
    }
    return { min: 0, max: 50 };
  };

  // Initial state
  const [localBudget, setLocalBudget] = useState({
    min: { value: toDisplayValue(budget.min), unit: getUnit(budget.min) },
    max: { value: toDisplayValue(budget.max), unit: getUnit(budget.max) }
  });
  const [selectedUnit, setSelectedUnit] = useState(getUnit(Math.max(budget.min, budget.max)));
  const [isModified, setIsModified] = useState(false);
  const [error, setError] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState({ min: '', max: '', range: '' });

  useEffect(() => {
    const unit = getUnit(Math.max(budget.min, budget.max));
    setLocalBudget({
      min: { value: toDisplayValue(budget.min), unit },
      max: { value: toDisplayValue(budget.max), unit }
    });
    setSelectedUnit(unit);
    setIsModified(false);
    setError('');
    setValidationErrors({ min: '', max: '', range: '' });
  }, [budget]);

  // Real-time validation
  useEffect(() => {
    const errors = { min: '', max: '', range: '' };
    const limits = getInputLimits(selectedUnit);
    
    const minValue = parseFloat(localBudget.min.value) || 0;
    const maxValue = parseFloat(localBudget.max.value) || 0;

    // Validate minimum value (allow 0)
    if (minValue < 0) {
      errors.min = `Minimum cannot be negative`;
    } else if (minValue > limits.max) {
      errors.min = `Maximum allowed is ${limits.max}${selectedUnit}`;
    }

    // Validate maximum value (allow 0)
    if (maxValue < 0) {
      errors.max = `Maximum cannot be negative`;
    } else if (maxValue > limits.max) {
      errors.max = `Maximum allowed is ${limits.max}${selectedUnit}`;
    }

    // Validate range (allow both to be 0, but if both > 0, max must be > min)
    if (minValue > 0 && maxValue > 0 && minValue >= maxValue) {
      errors.range = 'Maximum budget should be greater than minimum budget';
    }

    setValidationErrors(errors);
  }, [localBudget, selectedUnit]);

  const handleInputChange = (field, value) => {
    // Remove non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleanValue.split('.');
    if (parts.length > 2) return;
    
    // Limit decimal places to 1 for Cr, 0 for L
    if (parts[1] && parts[1].length > (selectedUnit === 'Cr' ? 1 : 0)) return;

    const numValue = parseFloat(cleanValue) || 0;
    let newValue = cleanValue;
    let newUnit = selectedUnit;

    // Auto-switch unit logic only when values exceed reasonable limits
    // Don't auto-switch if value is 0
    if (numValue === 0) {
      // Keep 0 in current unit
      newValue = '0';
    } else if (selectedUnit === 'L' && numValue >= 100) {
      // Switch from L to Cr when value >= 100L
      newValue = (numValue / 100).toFixed(1);
      newUnit = 'Cr';
    } else if (selectedUnit === 'Cr' && numValue < 1 && numValue > 0) {
      // Switch from Cr to L when value < 1Cr (but not 0)
      newValue = (numValue * 100).toFixed(0);
      newUnit = 'L';
    }

    setLocalBudget(prev => ({
      ...prev,
      [field]: { 
        ...prev[field], 
        value: newValue,
        unit: newUnit
      }
    }));

    // Update selected unit if auto-switched
    if (newUnit !== selectedUnit) {
      setSelectedUnit(newUnit);
    }

    setIsModified(true);
    setError('');
  };

  const handleUnitChange = (unit) => {
    // Don't convert values, just change the unit
    setLocalBudget(prev => ({
      min: { ...prev.min, unit },
      max: { ...prev.max, unit }
    }));
    setSelectedUnit(unit);
    setIsModified(true);
    setError('');
  };

  const handleIncrement = (field) => {
    const currentValue = parseFloat(localBudget[field].value) || 0;
    const step = getStepValue(selectedUnit);
    const limits = getInputLimits(selectedUnit);
    const newValue = Math.min(limits.max, currentValue + step).toFixed(selectedUnit === 'Cr' ? 1 : 0);
    
    setLocalBudget(prev => ({
      ...prev,
      [field]: { 
        ...prev[field], 
        value: newValue,
        unit: selectedUnit
      }
    }));
    setIsModified(true);
    setError('');
  };

  const handleDecrement = (field) => {
    const currentValue = parseFloat(localBudget[field].value) || 0;
    const step = getStepValue(selectedUnit);
    const limits = getInputLimits(selectedUnit);
    // Allow going down to 0
    const newValue = Math.max(0, currentValue - step).toFixed(selectedUnit === 'Cr' ? 1 : 0);
    
    setLocalBudget(prev => ({
      ...prev,
      [field]: { 
        ...prev[field], 
        value: newValue,
        unit: selectedUnit
      }
    }));
    setIsModified(true);
    setError('');
  };

  const validateBudget = () => {
    const minRupees = toRupees(localBudget.min.value, selectedUnit);
    const maxRupees = toRupees(localBudget.max.value, selectedUnit);

    // Allow 0 values, but validate ranges if values are provided
    if (minRupees < 0) {
      setError('Minimum budget cannot be negative');
      return false;
    }
    if (maxRupees < 0) {
      setError('Maximum budget cannot be negative');
      return false;
    }

    if (selectedUnit === 'L') {
      if (maxRupees > 100000000) {
        setError('Maximum budget should not exceed ₹10Cr');
        return false;
      }
    } else {
      if (maxRupees > 500000000) {
        setError('Maximum budget should not exceed ₹50Cr');
        return false;
      }
    }
    
    // Only validate range if both values are greater than 0
    if (minRupees > 0 && maxRupees > 0 && minRupees >= maxRupees) {
      setError('Minimum budget should be less than maximum budget');
      return false;
    }

    // Check for any validation errors
    if (Object.values(validationErrors).some(error => error !== '')) {
      setError('Please fix the validation errors before saving');
      return false;
    }

    return true;
  };

  const handleSaveClick = () => {
    if (!leadId || !isModified) return;

    if (!validateBudget()) return;

    setShowConfirmDialog(true);
  };

  const handleConfirmSave = async () => {
    const minValue = toRupees(localBudget.min.value, selectedUnit);
    const maxValue = toRupees(localBudget.max.value, selectedUnit);

    try {
      const budgetPayload = {
        values: {
          min: minValue,
          max: maxValue
        },
        type: "budget"
      };
      await updateLeadPreferences(leadId, budgetPayload.values, budgetPayload.type);
      setIsModified(false);
      setError('');
      setShowConfirmDialog(false);
    } catch (error) {
      console.error("Error updating budget:", error);
      setError('Failed to update budget. Please try again.');
      setShowConfirmDialog(false);
    }
  };

  const handleCancelSave = () => {
    setShowConfirmDialog(false);
  };

  const formatDisplayValue = (value, unit) => {
    return `${value}${unit}`;
  };

  const hasValidationErrors = Object.values(validationErrors).some(error => error !== '');
  const isRangeValid = !validationErrors.range;

  return (
    <SidebarSection 
      title="Budget"
      color="#4CAF50"
      icon={<CurrencyRupeeIcon fontSize="small" />}
      action={
        <Box sx={{ display: 'flex', gap: 0.8 }}>
          {isModified && (
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                const unit = getUnit(Math.max(budget.min, budget.max));
                setLocalBudget({
                  min: { value: toDisplayValue(budget.min), unit },
                  max: { value: toDisplayValue(budget.max), unit }
                });
                setSelectedUnit(unit);
                setIsModified(false);
                setError('');
                setValidationErrors({ min: '', max: '', range: '' });
              }}
              sx={{
                fontSize: '0.7rem',
                fontWeight: 600,
                py: 0.4,
                px: 1.2,
                minWidth: '50px',
                height: '26px',
                textTransform: 'none',
                borderRadius: 1,
                borderColor: '#666',
                color: '#666',
                '&:hover': {
                  borderColor: '#333',
                  backgroundColor: '#f5f5f5'
                }
              }}
            >
              Cancel
            </Button>
          )}
          <Button 
            variant="contained" 
            size="small"
            color="primary"
            disabled={!isModified || hasValidationErrors}
            onClick={handleSaveClick}
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              py: 0.4,
              px: 1.2,
              minWidth: '70px',
              height: '26px',
              textTransform: 'none',
              borderRadius: 1,
              opacity: (isModified && !hasValidationErrors) ? 1 : 0.6,
              backgroundColor: (isModified && !hasValidationErrors) ? '#4CAF50' : '#ccc',
              '&:hover': {
                backgroundColor: (isModified && !hasValidationErrors) ? '#45a049' : '#ccc'
              }
            }}
          >
            {isModified ? 'Save' : 'Saved'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ px: 0.8, py: 0.8 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1, py: 0.6, fontSize: '0.75rem' }}>
            {error}
          </Alert>
        )}
        
        {/* Unit Selection - Made more prominent */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.6, fontSize: '0.75rem' }}>
            Choose Unit:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.6 }}>
            <Button
              variant={selectedUnit === 'L' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleUnitChange('L')}
              sx={{
                flex: 1,
                py: 0.6,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: selectedUnit === 'L' ? '#00ACC1' : 'transparent',
                color: selectedUnit === 'L' ? '#fff' : '#00ACC1',
                borderColor: '#00ACC1',
                borderWidth: 1.5,
                '&:hover': {
                  backgroundColor: selectedUnit === 'L' ? '#0097A7' : '#E0F7FA',
                  borderWidth: 1.5
                }
              }}
            >
              Lakh (₹)
            </Button>
            <Button
              variant={selectedUnit === 'Cr' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleUnitChange('Cr')}
              sx={{
                flex: 1,
                py: 0.6,
                fontSize: '0.7rem',
                fontWeight: 600,
                backgroundColor: selectedUnit === 'Cr' ? '#00ACC1' : 'transparent',
                color: selectedUnit === 'Cr' ? '#fff' : '#00ACC1',
                borderColor: '#00ACC1',
                borderWidth: 1.5,
                '&:hover': {
                  backgroundColor: selectedUnit === 'Cr' ? '#0097A7' : '#E0F7FA',
                  borderWidth: 1.5
                }
              }}
            >
              Crore (₹)
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Minimum Budget */}
          <Box>
            <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.6, fontSize: '0.75rem' }}>
              Minimum Budget
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.6,
              p: 0.8,
              bgcolor: '#f8f9fa',
              borderRadius: 1,
              border: validationErrors.min ? '1.5px solid #ff6b6b' : '1.5px solid #e9ecef'
            }}>
              <IconButton
                size="small"
                onClick={() => handleDecrement('min')}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #dee2e6',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    bgcolor: '#e9ecef'
                  }
                }}
              >
                <RemoveIcon sx={{ fontSize: '12px' }} />
              </IconButton>
              
              <TextField
                size="small"
                type="number"
                value={localBudget.min.value}
                onChange={(e) => handleInputChange('min', e.target.value)}
                placeholder={selectedUnit === 'L' ? "0" : "0"}
                error={!!validationErrors.min}
                helperText={validationErrors.min}
                inputProps={{
                  step: getStepValue(selectedUnit),
                  min: getInputLimits(selectedUnit).min,
                  max: getInputLimits(selectedUnit).max,
                  style: { 
                    textAlign: 'center', 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#495057'
                  }
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    height: '28px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.7rem',
                    margin: '1px 0 0 0',
                    color: '#ff6b6b'
                  }
                }}
              />
              
              <IconButton
                size="small"
                onClick={() => handleIncrement('min')}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #dee2e6',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    bgcolor: '#e9ecef'
                  }
                }}
              >
                <AddIcon sx={{ fontSize: '12px' }} />
              </IconButton>
            </Box>
          </Box>

          {/* Maximum Budget */}
          <Box>
            <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 0.6, fontSize: '0.75rem' }}>
              Maximum Budget
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.6,
              p: 0.8,
              bgcolor: '#f8f9fa',
              borderRadius: 1,
              border: validationErrors.max ? '1.5px solid #ff6b6b' : '1.5px solid #e9ecef'
            }}>
              <IconButton
                size="small"
                onClick={() => handleDecrement('max')}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #dee2e6',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    bgcolor: '#e9ecef'
                  }
                }}
              >
                <RemoveIcon sx={{ fontSize: '12px' }} />
              </IconButton>
              
              <TextField
                size="small"
                type="number"
                value={localBudget.max.value}
                onChange={(e) => handleInputChange('max', e.target.value)}
                placeholder={selectedUnit === 'L' ? "0" : "0"}
                error={!!validationErrors.max}
                helperText={validationErrors.max}
                inputProps={{
                  step: getStepValue(selectedUnit),
                  min: getInputLimits(selectedUnit).min,
                  max: getInputLimits(selectedUnit).max,
                  style: { 
                    textAlign: 'center', 
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#495057'
                  }
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.75rem',
                    height: '28px',
                    bgcolor: '#fff',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: 'none'
                    }
                  },
                  '& .MuiFormHelperText-root': {
                    fontSize: '0.7rem',
                    margin: '1px 0 0 0',
                    color: '#ff6b6b'
                  }
                }}
              />
              
              <IconButton
                size="small"
                onClick={() => handleIncrement('max')}
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #dee2e6',
                  width: '24px',
                  height: '24px',
                  '&:hover': {
                    bgcolor: '#e9ecef'
                  }
                }}
              >
                <AddIcon sx={{ fontSize: '12px' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* Summary - Made more prominent */}
        <Box sx={{ 
          mt: 1, 
          p: 1, 
          bgcolor: isRangeValid ? '#e8f5e8' : '#fff3cd', 
          borderRadius: 1,
          border: `1.5px solid ${isRangeValid ? '#4CAF50' : '#ffc107'}`,
          position: 'relative'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.6 }}>
            {isRangeValid ? (
              <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: '16px' }} />
            ) : (
              <WarningIcon sx={{ color: '#ffc107', fontSize: '16px' }} />
            )}
            <Typography variant="body2" fontWeight={600} color={isRangeValid ? '#2e7d32' : '#856404'} sx={{ fontSize: '0.75rem' }}>
              {isRangeValid ? 'Valid Budget Range' : 'Invalid Budget Range'}
            </Typography>
          </Box>
          
          <Typography 
            variant="body2" 
            fontWeight={700} 
            color={isRangeValid ? "#2e7d32" : "#856404"}
            sx={{ textAlign: 'center', fontSize: '0.75rem' }}
          >
            ₹{formatDisplayValue(localBudget.min.value, selectedUnit)} - ₹{formatDisplayValue(localBudget.max.value, selectedUnit)}
          </Typography>
          
          {validationErrors.range && (
            <Typography variant="body2" color="#856404" sx={{ textAlign: 'center', mt: 0.6, fontWeight: 500, fontSize: '0.7rem' }}>
              {validationErrors.range}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelSave}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          fontSize: '1.2rem', 
          fontWeight: 600,
          color: 'text.primary',
          textAlign: 'center'
        }}>
          Confirm Budget Update
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Are you sure you want to update the budget preferences for this lead?
          </Typography>
          <Box sx={{ 
            p: 2, 
            bgcolor: '#e8f5e8', 
            borderRadius: 2,
            border: '2px solid #4CAF50',
            textAlign: 'center'
          }}>
            <Typography variant="body2" fontWeight={600} color="text.primary" sx={{ mb: 1 }}>
              New Budget Range:
            </Typography>
            <Typography variant="h5" color="#2e7d32" fontWeight={700}>
              ₹{formatDisplayValue(localBudget.min.value, selectedUnit)} - ₹{formatDisplayValue(localBudget.max.value, selectedUnit)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1, justifyContent: 'center', gap: 2 }}>
          <Button 
            onClick={handleCancelSave}
            variant="outlined"
            size="large"
            sx={{ 
              textTransform: 'none',
              borderColor: '#666',
              color: '#666',
              px: 3,
              '&:hover': {
                borderColor: '#333',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSave}
            variant="contained"
            size="large"
            sx={{ 
              textTransform: 'none',
              backgroundColor: '#4CAF50',
              px: 3,
              '&:hover': {
                backgroundColor: '#45a049'
              }
            }}
          >
            Confirm Update
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarSection>
  );
};

// --------------------------------------------------------------------------------------
// Redesigned budget section (matches `lead-details-redesign-v2 (13).html`)
// --------------------------------------------------------------------------------------
const BudgetSectionDesign = ({ budget = { min: 0, max: 0 }, leadId, variant = 'sidebar', readOnly = false }) => {
  const { updateLeadPreferences, fetchLeadPreference } = useLeadDetails();
  const isCard = variant === 'card';

  const T = {
    teal: '#0E9F7E',
    tealLight: '#ECFDF5',
    tealDark: '#065F46',
    border: '#E2E8F0',
    bg2: '#F1F5F9',
    slate2: '#1E293B',
    slate4: '#94A3B8',
    white: '#FFFFFF',
    slate: '#0F172A',
  };

  // Enforce the required absolute bounds:
  // - MIN >= 10L
  // - MAX <= 10Cr
  const getBounds = (u) => {
    if (u === 'crore') return { min: 0.1, max: 10 }; // 10Cr => 10
    return { min: 10, max: 100 }; // 10L => 10, 10Cr => 100L
  };
  const getStep = (u) => (u === 'crore' ? 0.1 : 1);
  const snap = (n, u) => {
    const num = Number(n);
    const { min, max } = getBounds(u);
    const step = getStep(u);
    if (!Number.isFinite(num)) return min;
    const rounded = Math.round(num / step) * step;
    const clamped = Math.max(min, Math.min(max, rounded));
    return u === 'crore' ? Math.round(clamped * 10) / 10 : Math.round(clamped);
  };

  const factor = (unit) => (unit === 'crore' ? 10000000 : 100000);
  const toDisplay = (rupees, unit) => (Number(rupees) || 0) / factor(unit);
  const fromDisplay = (val, unit) => Math.round((Number(val) || 0) * factor(unit));

  const [unit, setUnit] = React.useState('lakh'); // 'lakh' | 'crore'
  const [minVal, setMinVal] = React.useState(getBounds('lakh').min);
  const [maxVal, setMaxVal] = React.useState(getBounds('lakh').max);
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  useEffect(() => {
    const nextUnit = (Number(budget?.max) || 0) >= 10000000 ? 'crore' : 'lakh';
    setUnit(nextUnit);
    const nextMin = snap(toDisplay(budget?.min, nextUnit), nextUnit);
    const nextMax = snap(toDisplay(budget?.max, nextUnit), nextUnit);
    const minClamped = Math.min(nextMin, nextMax);
    const maxClamped = Math.max(nextMin, nextMax);
    setMinVal(minClamped);
    setMaxVal(maxClamped);
  }, [budget]);

  const { min: rangeMin, max: rangeMax } = getBounds(unit);
  const span = Math.max(0.000001, rangeMax - rangeMin);
  const leftPct = ((minVal - rangeMin) / span) * 100;
  const rightPct = ((maxVal - rangeMin) / span) * 100;

  const formatINR = (val, u) => {
    const suffix = u === 'crore' ? 'Cr' : 'L';
    const vv = Number(val) || 0;
    return `₹${vv} ${suffix}`;
  };

  const unitSx = (active) => ({
    flex: 1,
    py: isCard ? '3px' : '4px',
    fontSize: isCard ? '0.625rem' : '0.69rem',
    fontWeight: 600,
    fontFamily: 'inherit',
    border: 'none',
    borderRadius: '4px',
    cursor: readOnly ? 'default' : 'pointer',
    transition: 'all 0.15s',
    color: active ? T.tealDark : T.slate4,
    bgcolor: active ? T.white : 'transparent',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
    opacity: readOnly ? 0.75 : 1,
    ...(!readOnly && { '&:hover': { bgcolor: active ? T.white : T.border } }),
  });

  const handleUnitSwitch = (nextUnit) => {
    if (readOnly) return;
    const minR = fromDisplay(minVal, unit);
    const maxR = fromDisplay(maxVal, unit);
    setUnit(nextUnit);
    const nextMin = snap(toDisplay(minR, nextUnit), nextUnit);
    const nextMax = snap(toDisplay(maxR, nextUnit), nextUnit);
    const minClamped = Math.min(nextMin, nextMax);
    const maxClamped = Math.max(nextMin, nextMax);
    setMinVal(minClamped);
    setMaxVal(maxClamped);
  };

  const handleMin = (e) => {
    if (readOnly) return;
    const v = Math.min(Number(e.target.value), maxVal);
    const nextMin = snap(v, unit);
    setMinVal(nextMin);
    if (nextMin > maxVal) setMaxVal(nextMin);
  };

  const handleMax = (e) => {
    if (readOnly) return;
    const v = Math.max(Number(e.target.value), minVal);
    const nextMax = snap(v, unit);
    setMaxVal(nextMax);
    if (nextMax < minVal) setMinVal(nextMax);
  };

  const handleMinInput = (e) => {
    if (readOnly) return;
    const raw = Number(e.target.value);
    const snapped = snap(raw || 0, unit);
    setMinVal(snapped);
    if (snapped > maxVal) setMaxVal(snapped);
  };

  const handleMaxInput = (e) => {
    if (readOnly) return;
    const raw = Number(e.target.value);
    const snapped = snap(raw || 0, unit);
    setMaxVal(snapped);
    if (snapped < minVal) setMinVal(snapped);
  };

  const CheckSVG = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const handleSave = async () => {
    if (readOnly) return;
    if (!leadId) return;
    if (saving) return;
    setSaving(true);
    try {
      const minR = fromDisplay(minVal, unit);
      const maxR = fromDisplay(maxVal, unit);
      await updateLeadPreferences(leadId, { min: minR, max: maxR }, 'budget');
      await fetchLeadPreference(leadId);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        px: isCard ? 0 : '14px',
        py: isCard ? 0 : '12px',
        borderBottom: isCard ? 'none' : `1px solid ${T.border}`,
      }}
    >
      {!isCard && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: T.slate2,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ fontSize: '0.85rem' }}>💰</span>
            Budget
          </Typography>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: T.tealDark,
              bgcolor: T.tealLight,
              borderRadius: '6px',
              px: '8px',
              py: '3px',
              letterSpacing: '-0.02em',
            }}
          >
            {formatINR(minVal, unit)} – {formatINR(maxVal, unit)}
          </Typography>
        </Box>
      )}

      {isCard && (
        <Typography
          sx={{
            fontSize: '0.6875rem',
            fontWeight: 700,
            color: T.tealDark,
            bgcolor: T.tealLight,
            borderRadius: '5px',
            px: '8px',
            py: '4px',
            mb: 0.75,
            display: 'inline-block',
            letterSpacing: '-0.02em',
          }}
        >
          {formatINR(minVal, unit)} – {formatINR(maxVal, unit)}
        </Typography>
      )}

      {/* Unit toggle */}
      <Box
        sx={{
          display: 'flex',
          bgcolor: T.bg2,
          borderRadius: '6px',
          p: '2px',
          gap: '2px',
          mb: isCard ? 0.75 : 1.25,
        }}
      >
        <Box
          component="button"
          type="button"
          disabled={readOnly}
          sx={unitSx(unit === 'lakh')}
          onClick={() => handleUnitSwitch('lakh')}
        >
          Lakh ₹
        </Box>
        <Box
          component="button"
          type="button"
          disabled={readOnly}
          sx={unitSx(unit === 'crore')}
          onClick={() => handleUnitSwitch('crore')}
        >
          Crore ₹
        </Box>
      </Box>

      {/* Dual range slider */}
      <Box sx={{ position: 'relative', height: isCard ? 22 : 28, display: 'flex', alignItems: 'center', mb: isCard ? 0.75 : 1 }}>
        {/* Track */}
        <Box sx={{ position: 'absolute', left: 0, right: 0, height: '4px', bgcolor: T.border, borderRadius: '2px' }} />
        {/* Fill */}
        <Box
          sx={{
            position: 'absolute',
            left: `${leftPct}%`,
            width: `${rightPct - leftPct}%`,
            height: '4px',
            bgcolor: T.teal,
            borderRadius: '2px',
            pointerEvents: 'none',
          }}
        />

        {/* Min thumb */}
        <Box
          component="input"
          type="range"
          min={rangeMin}
          max={rangeMax}
          step={getStep(unit)}
          value={minVal}
          onInput={handleMin}
          disabled={readOnly}
          sx={{
            position: 'absolute',
            width: '100%',
            pointerEvents: 'none',
            WebkitAppearance: 'none',
            appearance: 'none',
            background: 'transparent',
            height: '4px',
            '&::-webkit-slider-thumb': {
              WebkitAppearance: 'none',
              width: isCard ? 13 : 16,
              height: isCard ? 13 : 16,
              borderRadius: '50%',
              background: T.white,
              border: `${isCard ? '2px' : '2.5px'} solid ${T.teal}`,
              cursor: 'pointer',
              pointerEvents: 'all',
              boxShadow: '0 1px 4px rgba(14,159,126,0.25)',
              transition: 'transform 0.1s',
              '&:hover': { transform: 'scale(1.15)' },
            },
          }}
        />

        {/* Max thumb */}
        <Box
          component="input"
          type="range"
          min={rangeMin}
          max={rangeMax}
          step={getStep(unit)}
          value={maxVal}
          onInput={handleMax}
          disabled={readOnly}
          sx={{
            position: 'absolute',
            width: '100%',
            pointerEvents: 'none',
            WebkitAppearance: 'none',
            appearance: 'none',
            background: 'transparent',
            height: '4px',
            '&::-webkit-slider-thumb': {
              WebkitAppearance: 'none',
              width: isCard ? 13 : 16,
              height: isCard ? 13 : 16,
              borderRadius: '50%',
              background: T.white,
              border: `${isCard ? '2px' : '2.5px'} solid ${T.teal}`,
              cursor: 'pointer',
              pointerEvents: 'all',
              boxShadow: '0 1px 4px rgba(14,159,126,0.25)',
              transition: 'transform 0.1s',
              '&:hover': { transform: 'scale(1.15)' },
            },
          }}
        />
      </Box>

      {/* Number inputs */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isCard ? '5px' : '6px' }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: isCard ? '0.55rem' : '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.slate4, mb: '2px' }}>
            MIN
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: `1.5px solid ${T.border}`,
              borderRadius: '6px',
              bgcolor: T.white,
              overflow: 'hidden',
              transition: 'border-color 0.15s',
              '&:focus-within': { borderColor: T.teal },
            }}
          >
            <Typography sx={{ fontSize: '0.69rem', fontWeight: 600, color: T.slate4, px: '6px' }}>₹</Typography>
            <Box
              component="input"
              type="number"
              value={minVal}
              min={rangeMin}
              max={rangeMax}
              step={getStep(unit)}
              onChange={handleMinInput}
              readOnly={readOnly}
              disabled={readOnly}
              sx={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: isCard ? '0.6875rem' : '0.75rem',
                fontWeight: 600,
                color: T.slate,
                fontFamily: 'inherit',
                p: isCard ? '4px 5px' : '5px 6px',
                background: 'transparent',
                width: '100%',
                minWidth: 0,
                textAlign: 'center',
              }}
            />
          </Box>
        </Box>

        <Typography sx={{ fontSize: '0.75rem', color: T.slate4, flexShrink: 0, mt: isCard ? '12px' : '14px' }}>—</Typography>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: isCard ? '0.55rem' : '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: T.slate4, mb: '2px' }}>
            MAX
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              border: `1.5px solid ${T.border}`,
              borderRadius: '6px',
              bgcolor: T.white,
              overflow: 'hidden',
              transition: 'border-color 0.15s',
              '&:focus-within': { borderColor: T.teal },
            }}
          >
            <Typography sx={{ fontSize: '0.69rem', fontWeight: 600, color: T.slate4, px: '6px' }}>₹</Typography>
            <Box
              component="input"
              type="number"
              value={maxVal}
              min={rangeMin}
              max={rangeMax}
              step={getStep(unit)}
              onChange={handleMaxInput}
              readOnly={readOnly}
              disabled={readOnly}
              sx={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: isCard ? '0.6875rem' : '0.75rem',
                fontWeight: 600,
                color: T.slate,
                fontFamily: 'inherit',
                p: isCard ? '4px 5px' : '5px 6px',
                background: 'transparent',
                width: '100%',
                minWidth: 0,
                textAlign: 'center',
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Save button */}
      {!readOnly && (
        <Box
          component="button"
          type="button"
          onClick={handleSave}
          disabled={saving}
          sx={{
            mt: isCard ? 0.875 : 1.25,
            width: '100%',
            py: isCard ? '6px' : '7px',
            borderRadius: '7px',
            background: saving ? '#16a34a66' : T.teal,
            color: '#fff',
            border: 'none',
            fontSize: isCard ? '0.6875rem' : '0.75rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            cursor: 'pointer',
            transition: 'background 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            '&:hover': { background: saving ? '#16a34a66' : T.tealDark },
          }}
        >
          <CheckSVG />
          Save Budget
        </Box>
      )}

      {saved && (
        <Box
          sx={{
            mt: isCard ? 0.5 : 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '5px',
            fontSize: isCard ? '0.625rem' : '0.7rem',
            fontWeight: 700,
            color: T.teal,
            opacity: 1,
          }}
        >
          <CheckSVG />
          Saved!
        </Box>
      )}
    </Box>
  );
};

export default BudgetSectionDesign;